/**
 * Celestia GameScreen — multiplayer store-wiring shell (SERVER-AUTHORITATIVE,
 * ADR-04).
 *
 * This component owns ONLY the network/store wiring: the per-user
 * `game:viewer_state` push, host/guest init, idempotent action submission,
 * and leaving. All rendering is delegated to the presentational
 * `GameScreenView`, which receives the server-projected `ViewerState` plus
 * typed action intents — it never sees raw `GameState`, a seed, or another
 * player's hand (the distinct `ViewerState` type is the compile-time guard).
 *
 * Each action carries a fresh `actionId` (UUID) for `(roomId, actionId)`
 * idempotency (spec §6.1); there is NO optimistic apply.
 */

import type { Action } from "@cloudnest/celestia-engine";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayNames } from "../../shared/utils/participants";
import { useLocale } from "./i18n/useLocale";
import {
  celestiaMultiplayerActions,
  useCelestiaMultiplayerStore,
} from "./multiplayer-game-store";
import { GameScreenView } from "./screen/GameScreenView";
import type { CelestiaActionHandlers } from "./screen/types";
import { createDisplayPrefsStore } from "./store/display-prefs";
import "./styles/celestia-tokens.css";
import "./styles/celestia-a11y.css";
import "./styles/celestia-screen.css";

function newActionId(): string {
  return crypto.randomUUID();
}

// Module-level singleton: one prefs store per GameScreen lifecycle.
const prefsRef = createDisplayPrefsStore();

export function GameScreen() {
  const prefs = useSyncExternalStore(prefsRef.subscribe, prefsRef.getSnapshot);
  const i18n = useLocale(prefs.locale);
  const { t } = i18n;

  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, myPlayerId } = useCelestiaMultiplayerStore();
  const actions = celestiaMultiplayerActions;

  const storedRoomId = currentRoom?.id ?? null;
  const storedIsHost = currentRoom?.hostUserId === user?.id;

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  // actions is the module-level celestiaMultiplayerActions singleton (never reassigned); omitted from deps intentionally.
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(storedRoomId, {
    onGameEvent: () => {
      // Celestia never replays opaque events client-side (ADR-04). The only
      // realtime state path is the per-user game:viewer_state push below.
    },
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (storedRoomId) void actionsRef.current.replayEvents(storedRoomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: storedRoomId captured once at mount; room_updated creates new object refs that would re-run init and wipe in-progress state.
  useEffect(() => {
    if (!storedRoomId) return;
    if (storedIsHost) {
      const playerIds = currentRoom?.participantUserIds ?? [];
      const playerNames = getParticipantDisplayNames(currentRoom, "Player");
      void actions.initializeAsHost(storedRoomId, playerIds, playerNames);
    } else {
      void actions.initializeAsGuest(storedRoomId);
    }
    return () => {
      actions.reset();
    };
  }, [storedRoomId, storedIsHost]);

  function leave() {
    actions.reset();
    navigateTo("/celestia/lobby");
  }

  const rootDataAttrs = {
    "data-celestia-root": "",
    "data-text-scale": prefs.textScale,
    "data-dyslexia-font": prefs.dyslexiaFont ? "true" : "false",
  } as const;

  if (!viewerState) {
    return (
      <div
        {...rootDataAttrs}
        data-testid="game-loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={t("a11y_loading")}
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--cn-celestia-bg)",
          color: "var(--cn-celestia-card-label)",
        }}
      >
        <span>{t("ui_loading")}</span>
      </div>
    );
  }

  const sv = viewerState.version;
  const pid = myPlayerId ?? "";
  function submit(action: Action) {
    void actions.submitAction(action);
  }

  const handlers: CelestiaActionHandlers = {
    roll: () =>
      submit({
        type: "RollDice",
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    confirmLanding: () =>
      submit({
        type: "ConfirmLanding",
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    declareIntent: (claim) =>
      submit({
        type: "DeclareIntent",
        claim,
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    decide: (decision) =>
      submit({
        type: decision === "stay" ? "DecideStay" : "DecideLeave",
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    eject: (targetPlayerId) =>
      submit(
        targetPlayerId
          ? {
              type: "PlayEjection",
              targetPlayerId,
              stateVersion: sv,
              actionId: newActionId(),
              playerId: pid,
            }
          : {
              type: "PassReaction",
              stateVersion: sv,
              actionId: newActionId(),
              playerId: pid,
            },
      ),
    declareResolve: (canResolve) =>
      submit({
        type: canResolve ? "DeclareCanResolve" : "DeclareCannotResolve",
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    reaction: (kind) => {
      if (kind === "jetpack") {
        submit({
          type: "PlayJetpack",
          stateVersion: sv,
          actionId: newActionId(),
          playerId: pid,
        });
      } else if (kind === "windGust") {
        submit({
          type: "PlayWindGust",
          stateVersion: sv,
          actionId: newActionId(),
          playerId: pid,
        });
      } else {
        // Alternative Route rerolls one die; the new IA does not surface a
        // die picker, so default to the first die (engine validates).
        submit({
          type: "PlayAlternativeRoute",
          dieIdx: 0,
          stateVersion: sv,
          actionId: newActionId(),
          playerId: pid,
        });
      }
    },
    pass: () =>
      submit({
        type: "PassReaction",
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    payEquipment: (cardIds) =>
      submit({
        type: "PayEquipment",
        cards: cardIds,
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    useSpyglass: () =>
      submit({
        type: "UseMagicSpyglass",
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
    takeTreasure: (cityId) =>
      submit({
        type: "ChooseTreasureCard",
        cityId,
        stateVersion: sv,
        actionId: newActionId(),
        playerId: pid,
      }),
  };

  return (
    <GameScreenView
      viewerState={viewerState}
      myPlayerId={myPlayerId}
      connectionStatus={connectionStatus}
      i18n={i18n}
      prefs={prefs}
      actions={handlers}
      onLeave={leave}
      lastError={lastError?.error ?? null}
      onClearError={() => actions.clearError()}
      onSetLocale={prefsRef.setLocale}
      onToggleDyslexia={prefsRef.toggleDyslexiaFont}
      onSetTextScale={prefsRef.setTextScale}
    />
  );
}
